import React, { useState, useMemo } from 'react';
import { Box, IconButton, Paper, Typography, Button } from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import './TokenCarousel.css';

const TokenCarousel = ({ adminTokens, onMakeDefault }) => {
  // Convert adminTokens into an array of token objects.
  const tokens = useMemo(() => {
    if (
      typeof adminTokens === 'object' &&
      adminTokens !== null &&
      !Array.isArray(adminTokens)
    ) {
      return Object.entries(adminTokens).reduce((acc, [account, tokenList]) => {
        tokenList.forEach((token) => {
          acc.push({ account, token, isDefault: token.endsWith('*') });
        });
        return acc;
      }, []);
    } else if (Array.isArray(adminTokens)) {
      return adminTokens;
    }
    return [];
  }, [adminTokens]);

  // Assume tokens is non-empty.
  const [currentIndex, setCurrentIndex] = useState(0);
  // "shift" indicates an animation is in progress.
  // It will be 'left' when the left arrow is clicked and 'right' when the right arrow is clicked.
  const [shift, setShift] = useState(null);
  const [animating, setAnimating] = useState(false);

  // Compute indices for the circular carousel.
  const getNextIndex = (index = currentIndex) =>
    index === tokens.length - 1 ? 0 : index + 1;
  const getPrevIndex = (index = currentIndex) =>
    index === 0 ? tokens.length - 1 : index - 1;

  const prevIndex = getPrevIndex();
  const nextIndex = getNextIndex();
  // For left arrow, newNextIndex is the token that will appear as the new next token.
  const newNextIndex = getNextIndex(nextIndex);
  // For right arrow, newPrevIndex is the token that will appear as the new previous token.
  const newPrevIndex = getPrevIndex(prevIndex);

  // Left arrow handler (as before).
  const handleLeft = () => {
    if (animating) return;
    setAnimating(true);
    setShift('left');
    setTimeout(() => {
      setCurrentIndex(nextIndex);
      setShift(null);
      setAnimating(false);
    }, 300);
  };

  // Right arrow handler.
  const handleRight = () => {
    if (animating) return;
    setAnimating(true);
    setShift('right');
    setTimeout(() => {
      setCurrentIndex(prevIndex);
      setShift(null);
      setAnimating(false);
    }, 300);
  };

  const currentToken = tokens[currentIndex];

  return (
    <Paper
      elevation={3}
      sx={{
        padding: 2,
        margin: 1,
        minWidth: 400,
        flex: '1 1 auto',
        minHeight: 0,
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'center',
      }}
    >
      {/* Left arrow button */}
      <IconButton onClick={handleLeft} aria-label="move carousel left">
        <ArrowBackIosIcon sx={{ color: 'black' }} />
      </IconButton>

      <Box sx={{ flex: 1, textAlign: 'center' }}>
        <Typography variant="subtitle2" color="textSecondary">
          Account: {currentToken.account}
        </Typography>

        <Box className="carousel-container" sx={{ mt: 1 }}>
          <Box className="carousel-inner">
            {/* Token in previous slot */}
            <Box
              className={`carousel-item token-prev ${
                shift === 'left'
                  ? 'prev-shift-out-left'
                  : shift === 'right'
                  ? 'prev-shift-right'
                  : ''
              }`}
            >
              <Typography variant="h5">
                {tokens[prevIndex].token}
              </Typography>
            </Box>

            {/* Focused token */}
            <Box
              className={`carousel-item token-current ${
                shift === 'left'
                  ? 'focus-shift-left'
                  : shift === 'right'
                  ? 'focus-shift-right'
                  : ''
              }`}
            >
              <Typography variant="h5">
                {currentToken.token}
              </Typography>
            </Box>

            {/* Token in next slot */}
            <Box
              className={`carousel-item token-next ${
                shift === 'left' ? 'next-shift-left' : shift === 'right' ? 'next-shift-right' : ''
              }`}
            >
              <Typography variant="h5">
                {tokens[nextIndex].token}
              </Typography>
            </Box>

            {/* NEW element for left arrow: new next token */}
            {shift === 'left' && (
              <Box className="carousel-item token-new-next shift-in-left">
                <Typography variant="h5">
                  {tokens[newNextIndex].token}
                </Typography>
              </Box>
            )}

            {/* NEW element for right arrow: new previous token */}
            {shift === 'right' && (
              <Box className="carousel-item token-new-prev shift-in-right">
                <Typography variant="h5">
                  {tokens[newPrevIndex].token}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {currentToken.isDefault ? (
          <Typography variant="caption" color="primary" sx={{ mt: 1 }}>
            Default
          </Typography>
        ) : (
          <Button
            variant="outlined"
            size="small"
            onClick={() => onMakeDefault(currentIndex)}
            sx={{ mt: 1 }}
          >
            Make Default
          </Button>
        )}
      </Box>

      {/* Right arrow button */}
      <IconButton onClick={handleRight} aria-label="move carousel right">
        <ArrowForwardIosIcon sx={{ color: 'black' }} />
      </IconButton>
    </Paper>
  );
};

export default TokenCarousel;
